import { useEffect, useRef, useState } from 'react';

/**
 * O seletor de projeto da barra lateral.
 *
 * <p>Era um {@code <select>} nativo. O campo fechado até que dava para estilizar, mas a
 * <b>lista aberta</b> quem desenha é o sistema operacional: no Windows a opção sob o
 * cursor vinha num azul claro de caixa de diálogo, que não é cor nenhuma deste produto —
 * e não há CSS que alcance aquilo. Por isso a lista virou um menu nosso.
 *
 * <p>O que se ganha junto: dá para pôr no menu uma opção que não é um projeto.
 *
 * <p>O que se perdeu, e que este arquivo devolve à mão: tudo o que o {@code <select>}
 * fazia de graça no teclado. Setas percorrem, Home e End vão às pontas, Esc fecha
 * devolvendo o foco ao botão, Tab sai. Sem isso, quem abre a lista pelo teclado fica
 * preso numa caixa que não responde — pior do que o azul do Windows.
 */

/**
 * As opções do menu, e a ordem em que as setas as percorrem.
 *
 * <p>São dados, e não JSX repetido, porque a navegação precisa saber quantas são e
 * quais são as pontas. Com o JSX escrito à mão duas vezes, acrescentar um projeto
 * exigiria lembrar de mexer também num contador — e ninguém lembraria.
 */
const OPCOES = [
  { chave: 'mistik-island', rotulo: 'Mistik Island', atual: true },
  { chave: 'novo', rotulo: 'Adicionar Projeto', atual: false },
] as const;

export function SeletorDeProjeto() {
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const [safado, setSafado] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const itens = useRef<(HTMLButtonElement | null)[]>([]);

  /** Fecha devolvendo o foco ao botão: sem isso o foco volta ao começo da página. */
  function fechar() {
    setAberto(false);
    botao.current?.focus();
  }

  // Clique fora e Esc fecham o menu — é o que um <select> fazia de graça, e o que
  // qualquer um espera de uma lista aberta.
  useEffect(() => {
    if (!aberto) return;
    const foraDaCaixa = (e: MouseEvent) => {
      if (!caixa.current?.contains(e.target as Node)) setAberto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar();
    };
    document.addEventListener('mousedown', foraDaCaixa);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa);
      document.removeEventListener('keydown', escape);
    };
  }, [aberto]);

  // O foco segue o item ativo. É o que faz a seta "andar": não há realce próprio de
  // teclado, o realce é o do foco — um estado só, e por isso nunca dessincronizado.
  useEffect(() => {
    if (aberto) itens.current[ativo]?.focus();
  }, [aberto, ativo]);

  function abrir(indice: number) {
    setAtivo(indice);
    setAberto(true);
  }

  function teclasDoBotao(e: React.KeyboardEvent) {
    // Enter e espaço já disparam o onClick do botão; só as setas precisam de código.
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      abrir(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      abrir(OPCOES.length - 1);
    }
  }

  function teclasDoItem(e: React.KeyboardEvent, indice: number) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAtivo((indice + 1) % OPCOES.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAtivo((indice - 1 + OPCOES.length) % OPCOES.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setAtivo(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setAtivo(OPCOES.length - 1);
    } else if (e.key === 'Tab') {
      // Tab fecha mas não puxa o foco de volta: quem tabula quer seguir adiante.
      setAberto(false);
    }
  }

  function escolher(chave: string) {
    setAberto(false);
    botao.current?.focus();
    if (chave === 'novo') setSafado(true);
  }

  return (
    <div className="proj" ref={caixa}>
      <span className="pl">Projeto</span>

      <div className="proj-campo">
        {/*
          Menu, e não listbox: uma das entradas é uma ação ("Adicionar Projeto"), e não
          um valor que se possa escolher. Menu também é o papel que deixa os itens
          receberem foco de verdade — em listbox quem percorre é o `aria-activedescendant`,
          e aí o realce e o foco viram dois estados que podem discordar.
        */}
        <button
          ref={botao}
          type="button"
          className={`proj-botao${aberto ? ' aberto' : ''}`}
          onClick={() => (aberto ? fechar() : abrir(0))}
          onKeyDown={teclasDoBotao}
          aria-haspopup="menu"
          aria-expanded={aberto}
        >
          Mistik Island
          <span className="proj-seta" aria-hidden="true" />
        </button>

        {aberto && (
          <ul className="proj-lista" role="menu">
            {OPCOES.map((opcao, indice) => (
              <li key={opcao.chave} role="none">
                <button
                  ref={(el) => {
                    itens.current[indice] = el;
                  }}
                  type="button"
                  role="menuitemradio"
                  aria-checked={opcao.atual}
                  className={`proj-op${opcao.atual ? ' on' : ''}${
                    opcao.chave === 'novo' ? ' novo' : ''
                  }`}
                  tabIndex={indice === ativo ? 0 : -1}
                  onKeyDown={(e) => teclasDoItem(e, indice)}
                  onMouseEnter={() => setAtivo(indice)}
                  onClick={() => escolher(opcao.chave)}
                >
                  {opcao.chave === 'novo' && (
                    <span className="proj-mais" aria-hidden="true">
                      +
                    </span>
                  )}
                  {opcao.rotulo}
                  {opcao.atual && (
                    <span className="proj-check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {safado && <AindaNao aoFechar={() => setSafado(false)} />}
    </div>
  );
}

/**
 * O bloqueio bem-humorado de "Adicionar Projeto".
 *
 * <p>Um segundo projeto hoje seria um convite a dividir atenção, e a resposta do Theo é
 * essa. Como o recado é uma piada interna, ele merece a cara de uma carta do baralho —
 * selo de cera, moldura dourada e o texto em fonte de título — e não a de um alerta de
 * erro. Um {@code window.alert()} diria a mesma frase e mataria a graça.
 */
function AindaNao({ aoFechar }: { aoFechar(): void }) {
  useEffect(() => {
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [aoFechar]);

  return (
    <div
      className="safado-fundo"
      onClick={(e) => e.target === e.currentTarget && aoFechar()}
      role="presentation"
    >
      <div className="safado" role="alertdialog" aria-modal="true" aria-label="Ainda não">
        <div className="safado-selo" aria-hidden="true">
          ✋
        </div>
        <p className="safado-texto">
          Ainda não, vai terminar a Season 1 de Mistik&nbsp;Island, <b>SAFADO!</b>
        </p>
        <button className="safado-ok" onClick={aoFechar} autoFocus>
          Tá bom, tá bom
        </button>
      </div>
    </div>
  );
}
